import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger, Separator } from "@adora/shadcn-ui";
import { cn } from "@adora/shadcn-ui/lib/utils";
import { ChevronsUpDown } from "lucide-react";

interface SelectOption {
  value: string;
  label: string;
  subLabel?: string;
}

interface CommonSelectorProps {
  selectOptions: readonly string[] | readonly SelectOption[];
  defaultValue?: string | SelectOption;
  onChange?: (value: string) => void;
  width?: string | number;
  fullHeight?: boolean;
  customDisplayValue?: string;
  customOptionLabel?: string;
  customComponent?:
    | React.ReactNode
    | ((props: { anchorEl: HTMLElement | null; onClose: () => void }) => React.ReactNode);

  maxWidth?: string | number;
  showDividerBeforeCustomOption?: boolean;
  size?: "small" | "medium";
  displayPrefix?: string;
  isSelected?: boolean;
  /** Tailwind rounding classes for the trigger (e.g. "rounded-l-md rounded-r-none"). Defaults to fully rounded. */
  roundedClassName?: string;
  /** Tailwind height class for the trigger. Defaults to the size-based height. */
  heightClassName?: string;
}

export const CommonSelector = ({
  selectOptions,
  defaultValue = "",
  onChange,
  width = "236px",
  maxWidth = "400px",
  fullHeight = true,
  customDisplayValue = undefined,
  customComponent,
  customOptionLabel = "Custom",
  showDividerBeforeCustomOption = true,
  size = "small",
  displayPrefix = "By",
  isSelected = false,
  roundedClassName = "rounded-md",
  heightClassName
}: CommonSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [showCustomPanel, setShowCustomPanel] = useState(false);
  const [value, setValue] = useState<string>(() => {
    if (typeof defaultValue === "string") return defaultValue;
    return defaultValue?.value || "";
  });
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Helper functions to handle both string[] and SelectOptions[] types
  const getOptionValue = useCallback((option: string | SelectOption): string => {
    return typeof option === "string" ? option : option.value;
  }, []);

  const getOptionLabel = useCallback((option: string | SelectOption): string => {
    return typeof option === "string" ? option : option.label;
  }, []);

  const getOptionSubLabel = useCallback((option: string | SelectOption): string | null => {
    return typeof option === "string" ? null : (option.subLabel ?? null);
  }, []);

  // Update internal value when defaultValue changes
  useEffect(() => {
    const nextValue = typeof defaultValue === "string" ? defaultValue : (defaultValue?.value ?? "");
    setValue(nextValue);
  }, [defaultValue]);

  const regularOptions = useMemo(
    () => selectOptions.filter((option) => getOptionValue(option) !== customOptionLabel),
    [selectOptions, customOptionLabel, getOptionValue]
  );

  const hasCustomOption = useMemo(
    () => selectOptions.some((option) => getOptionValue(option) === customOptionLabel),
    [selectOptions, customOptionLabel, getOptionValue]
  );

  // Handle option click - this fires for EVERY click, even re-selecting same value
  const handleOptionClick = useCallback(
    (optionValue: string) => {
      if (optionValue === customOptionLabel && customComponent) {
        // Open custom panel (e.g., DateRangePicker) - always works!
        setShowCustomPanel(true);
        setOpen(false);
      } else {
        setValue(optionValue);
        onChange?.(optionValue);
        setOpen(false);
      }
    },
    [customOptionLabel, customComponent, onChange]
  );

  // Handle custom panel close
  const handleCustomClose = useCallback(() => {
    requestAnimationFrame(() => {
      setShowCustomPanel(false);
    });
  }, []);

  // Render custom component with anchor element if it's a function
  const renderCustomComponent = useCallback(() => {
    if (!customComponent) return null;

    if (typeof customComponent === "function") {
      return customComponent({
        anchorEl: triggerRef.current,
        onClose: handleCustomClose
      });
    }

    return customComponent;
  }, [customComponent, handleCustomClose]);

  // Determine display value
  const displayValue = useMemo(() => {
    if (customDisplayValue) return customDisplayValue;
    return displayPrefix ? `${displayPrefix} ${value}` : value;
  }, [customDisplayValue, displayPrefix, value]);

  // Compute width style
  const widthStyle = typeof width === "number" ? `${width}px` : width;
  const maxWidthStyle = typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;

  // Size-based styles
  const heightClass = heightClassName ?? (size === "small" ? "h-8" : "h-10");

  return (
    <div
      className="relative"
      style={{
        width: widthStyle,
        minWidth: widthStyle,
        maxWidth: maxWidthStyle,
        height: fullHeight ? "100%" : undefined
      }}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            className={cn(
              "flex w-full items-center justify-between border border-gray-300 pl-3 pr-1.5",
              isSelected ? "bg-[rgba(100,100,100,0.15)]" : "bg-white",
              "text-left capitalize text-gray-900 transition-colors",
              "hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1",
              "text-xs font-light",
              roundedClassName,
              heightClass
            )}
            type="button"
          >
            <span className="inline-flex items-center gap-2 truncate">
              <span className="truncate">{displayValue}</span>
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-600" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-max min-w-[var(--radix-popover-trigger-width)] bg-white p-1 shadow-[0px_5px_5px_-3px_rgba(0,0,0,0.2),0px_8px_10px_1px_rgba(0,0,0,0.14),0px_3px_14px_2px_rgba(0,0,0,0.12)]"
          align="start"
          sideOffset={4}
        >
          <div className="flex flex-col">
            {regularOptions.map((option) => {
              const optionValue = getOptionValue(option);
              const optionLabel = getOptionLabel(option);
              const optionSubLabel = getOptionSubLabel(option);
              const isCustomDisplayActive = Boolean(customDisplayValue) && hasCustomOption;
              const isSelected = !isCustomDisplayActive && optionValue === value;

              return (
                <button
                  key={optionValue}
                  type="button"
                  onClick={() => handleOptionClick(optionValue)}
                  className={cn(
                    "flex w-full flex-col items-start whitespace-nowrap rounded-sm px-3 py-2 text-left text-sm capitalize",
                    "border-l-4 border-transparent transition-colors",
                    "hover:bg-gray-100",
                    isSelected && "border-l-orange-500 bg-gray-100"
                  )}
                >
                  <span className="text-gray-900">{optionLabel}</span>
                  {optionSubLabel && <span className="text-xs text-gray-500">{optionSubLabel}</span>}
                </button>
              );
            })}

            {hasCustomOption && regularOptions.length > 0 && showDividerBeforeCustomOption && (
              <Separator className="my-1" />
            )}

            {hasCustomOption && (
              <button
                type="button"
                onClick={() => handleOptionClick(customOptionLabel)}
                className={cn(
                  "flex w-full items-start whitespace-nowrap rounded-sm px-3 py-2 text-left text-sm capitalize",
                  "border-l-4 border-transparent transition-colors",
                  "hover:bg-gray-100",
                  (!!customDisplayValue || value === customOptionLabel) && "border-l-orange-500 bg-gray-100"
                )}
              >
                {customOptionLabel}...
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Custom component (e.g., DateRangePicker) rendered when panel is open */}
      {showCustomPanel && renderCustomComponent()}
    </div>
  );
};
